import React from 'react';
import {Box, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import PartnerDashboardShell from '../components/PartnerDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {NAVY, PageHeader, headCell, bodyCell} from '../components/widgets.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, kpiText, rowsFrom} from '../../DashboardCommon/dashboardCards';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

const GREEN = '#1B6B55';
const BLUE = '#3B6FA0';

/* Slide 23 — Program Deployment. Live data (Data Map PD-23-01 … 09):
     My Programs       PD.S_20.C_2 Active Programs · C_3 Schools Assigned · C_4 Program Completion
     Teacher-created   PD.S_20.C_6 · C_7 · C_8
     Program list      PD.S_20.C_10
   "Priority Curriculum Coverage" (PD-23-04 / -08) is not drawn — the Data Map
   marks it removed from the datahouse output by D-10. Same layout as the
   Network dashboard's live Program Deployment page. Every column of the
   program list sorts and filters from its header (QA 9 Oct 2026). */
const statsFor = (active, schools, completion) => [
    {label: 'Active Programs', value: kpiText(active), sub: 'deployed / created', accent: GREEN},
    {label: 'Schools Assigned', value: kpiText(schools), accent: BLUE},
    {label: 'Program Completion', value: kpiText(completion, true), accent: BLUE},
];

const StatCardAccent = ({label, value, sub, accent}) => (
    <Box sx={{
        display: 'flex', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px',
        backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden', height: '100%',
    }}>
        <Box sx={{width: '5px', flexShrink: 0, backgroundColor: accent}}/>
        <Box sx={{p: 2, flex: 1}}>
            <Typography sx={{
                fontSize: '10.5px', fontWeight: 700, color: '#6A7282', letterSpacing: '0.6px',
                textTransform: 'uppercase', mb: 1,
            }}>
                {label}
            </Typography>
            <Typography sx={{fontSize: '26px', fontWeight: 800, color: NAVY, lineHeight: 1}}>
                {value}
            </Typography>
            {sub && <Typography sx={{fontSize: '11px', color: '#9CA3AF', mt: 0.75}}>{sub}</Typography>}
        </Box>
    </Box>
);

const DeploymentStats = ({stats}) => (
    <Grid container spacing={2.5} sx={{mb: 4}}>
        {stats.map((s) => (
            <Grid item xs={12} sm={6} md={4} key={s.label}>
                <StatCardAccent {...s}/>
            </Grid>
        ))}
    </Grid>
);

const rateColor = (rate) => {
    const num = parseFloat(rate);
    if (isNaN(num)) return '#374151';
    return num >= 75 ? GREEN : '#E8730C';
};

const PROGRAM_COLUMNS = [
    {key: 'program', label: 'Program', type: 'text', align: 'left'},
    {key: 'creator', label: 'Program Creator', type: 'text', align: 'left'},
    {key: 'targetArea', label: 'Target Area', type: 'text', align: 'left'},
    {key: 'countries', label: 'Countries', type: 'number'},
    {key: 'schools', label: 'Schools', type: 'number'},
    {key: 'enrolled', label: 'Students Enrolled', type: 'number'},
    {key: 'completed', label: 'Students Completed', type: 'number'},
    {key: 'rate', label: 'Completion Rate', type: 'percent'},
    {key: 'status', label: 'Status', type: 'band'},
];

// The table sits in its own component so the sort/filter hook runs below the page's loading branches.
const ProgramsTable = ({rows}) => {
    const t = useTableControls(rows, PROGRAM_COLUMNS);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table sx={{minWidth: 1000}}>
                    <TableHead>
                        <TableRow>
                            {PROGRAM_COLUMNS.map((c, i) => (
                                <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: c.align || 'center', ...(i === 0 ? {pl: 2.5} : {})}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>No programs created or deployed yet.</TableCell>
                            </TableRow>
                        )}
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>No program matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.program}-${rows.indexOf(row)}`} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.program}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.creator}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.targetArea}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.countries}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.schools}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.enrolled}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.completed}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700, color: rateColor(row.rate)}}>{row.rate}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.status}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const PROGRAM_DEPLOYMENT_SECTION_CODE = 'S_20';
const CARD_SECTIONS = [PROGRAM_DEPLOYMENT_SECTION_CODE];

const ProgramDeploymentPage = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const card = (c) => findCard(dashboardData, PROGRAM_DEPLOYMENT_SECTION_CODE, c);
    const rows = rowsFrom(card('C_10'), {
        program: 'Program', creator: 'Program Creator', targetArea: 'Target Area', countries: 'Countries',
        schools: 'Schools', enrolled: 'Students Enrolled', completed: 'Students Completed',
        rate: ['Completion Rate %', 'Completion Rate'], status: 'Status',
    });

    if (loading) {
        return (
            <PartnerDashboardShell>
                <CircularLoader />
            </PartnerDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <PartnerDashboardShell>
                <NoDataAvailable />
            </PartnerDashboardShell>
        );
    }

    return (
    <PartnerDashboardShell>
        <PageHeader
            title="Program Deployment"
            subtitle="Are recommended programs actually being created, assigned, completed, and assessed?"
        />

        <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            My Programs
        </Typography>
        <DeploymentStats stats={statsFor(card('C_2'), card('C_3'), card('C_4'))}/>

        <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            Teacher-Created Programs
        </Typography>
        <DeploymentStats stats={statsFor(card('C_6'), card('C_7'), card('C_8'))}/>

        <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            List of Total Programs and Deployment Status
        </Typography>
        {/* "Shows all deployed + created programs." was a build note on the mockup, not page copy (PD slide 49). */}
        <Box sx={{mb: 2}}/>
        <ProgramsTable rows={rows}/>
    </PartnerDashboardShell>
    );
};

export default ProgramDeploymentPage;
